import { ArrowDown, ArrowUp, ChevronsUpDown, Columns3, EllipsisVertical, type LucideIcon } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import type { SortOrder } from "@/api/types";
import { EmptyState } from "@/components/feedback/empty-state";
import { ErrorPanel } from "@/components/feedback/query-state";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { Pagination } from "./pagination";

export interface Column<T> {
  id: string;
  header: string;
  cell: (row: T) => ReactNode;
  /** Server sort key; the column is sortable when set. */
  sortKey?: string;
  align?: "left" | "right";
  className?: string;
  /** Hidden until the user turns it on. */
  defaultHidden?: boolean;
  /** Primary column can't be hidden. */
  alwaysVisible?: boolean;
}

export interface RowAction<T> {
  label: string;
  icon?: LucideIcon;
  onSelect: (row: T) => void;
  destructive?: boolean;
  hidden?: (row: T) => boolean;
}

export interface AdminTableProps<T> {
  /** Stable id: used for column-visibility preferences. */
  id: string;
  label: string;
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  isPending: boolean;
  isFetching?: boolean;
  error?: unknown;
  onRetry?: () => void;
  sort?: string;
  order?: SortOrder;
  onSortChange?: (sort: string, order: SortOrder) => void;
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  selectable?: boolean;
  bulkActions?: (selected: T[], clear: () => void) => ReactNode;
  rowActions?: RowAction<T>[];
  onRowClick?: (row: T) => void;
  empty: { icon: LucideIcon; title: string; description?: ReactNode };
  toolbar?: ReactNode;
  resourceName?: string;
  dense?: boolean;
}

function useColumnVisibility(tableId: string, columns: Array<{ id: string; defaultHidden?: boolean }>) {
  const storageKey = `rogue.admin.columns.${tableId}`;
  const [hidden, setHidden] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch {
      /* per-viewer preference only; defaults are fine */
    }
    return new Set(columns.filter((c) => c.defaultHidden).map((c) => c.id));
  });
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify([...hidden]));
    } catch {
      /* ignore */
    }
  }, [hidden, storageKey]);
  const toggle = (id: string) =>
    setHidden((h) => {
      const n = new Set(h);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  return { hidden, toggle };
}

export function ColumnVisibility<T>({ columns, hidden, onToggle }: { columns: Column<T>[]; hidden: Set<string>; onToggle: (id: string) => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger render={<Button variant="outline" size="sm" aria-label="Choose columns" />}>
        <Columns3 />
        Columns
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Visible columns</DropdownMenuLabel>
          {columns
            .filter((c) => !c.alwaysVisible)
            .map((c) => (
              <DropdownMenuCheckboxItem key={c.id} checked={!hidden.has(c.id)} onCheckedChange={() => onToggle(c.id)} closeOnClick={false}>
                {c.header}
              </DropdownMenuCheckboxItem>
            ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SortHeader({ label, active, order, onClick }: { label: string; active: boolean; order?: SortOrder; onClick: () => void }) {
  const Icon = !active ? ChevronsUpDown : order === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "-mx-1 inline-flex items-center gap-1 rounded px-1 uppercase tracking-wide hover:text-foreground focus-visible:outline-2",
        active && "text-foreground",
      )}
    >
      {label}
      <Icon className={cn("size-3", !active && "opacity-40")} aria-hidden />
    </button>
  );
}

/**
 * Admin data table: server-side sort/filter/pagination (the parent owns URL state), column
 * visibility, bulk selection, row actions, and explicit loading/empty/error states.
 */
export function AdminTable<T>(props: AdminTableProps<T>) {
  const {
    id,
    label,
    columns,
    rows,
    rowKey,
    isPending,
    error,
    onRetry,
    sort,
    order,
    onSortChange,
    selectable,
    bulkActions,
    rowActions,
    onRowClick,
    empty,
    toolbar,
    dense = true,
  } = props;
  const { hidden, toggle } = useColumnVisibility(id, columns);
  const visible = columns.filter((c) => c.alwaysVisible || !hidden.has(c.id));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const pageKeys = useMemo(() => (rows ?? []).map(rowKey), [rows, rowKey]);
  // Selection is per page of results.
  const pageSignature = pageKeys.join("|");
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when the visible page changes
  useEffect(() => setSelected(new Set()), [pageSignature]);
  const selectedRows = (rows ?? []).filter((r) => selected.has(rowKey(r)));
  const allChecked = pageKeys.length > 0 && pageKeys.every((k) => selected.has(k));
  const colCount = visible.length + (selectable ? 1 : 0) + (rowActions?.length ? 1 : 0);

  return (
    <div className="flex min-h-0 flex-col rounded-md border bg-card">
      <div className="flex flex-wrap items-center gap-2 border-b px-2 py-1.5">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{toolbar}</div>
        <ColumnVisibility columns={columns} hidden={hidden} onToggle={toggle} />
      </div>
      {selectable && selectedRows.length > 0 && bulkActions ? (
        <section className="flex items-center gap-2 border-b bg-info-muted/60 px-3 py-1.5 text-xs" aria-label="Bulk actions">
          <span className="font-medium">{selectedRows.length} selected</span>
          {bulkActions(selectedRows, () => setSelected(new Set()))}
          <Button size="xs" variant="ghost" onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </section>
      ) : null}
      <div className="min-h-0 flex-1 overflow-auto">
        <Table aria-label={label} className={cn("text-[13px]", dense && "[&_td]:py-1.5 [&_th]:h-8")}>
          <TableHeader className="sticky top-0 z-10 bg-muted/60 backdrop-blur">
            <TableRow className="hover:bg-transparent">
              {selectable ? (
                <TableHead className="w-8 pl-3">
                  <Checkbox
                    aria-label="Select all rows on this page"
                    checked={allChecked}
                    onCheckedChange={(v) => setSelected(v ? new Set(pageKeys) : new Set())}
                  />
                </TableHead>
              ) : null}
              {visible.map((c, i) => (
                <TableHead
                  key={c.id}
                  className={cn("text-[11px] uppercase tracking-wide", c.align === "right" && "text-right", i === 0 && !selectable && "pl-3", c.className)}
                  aria-sort={c.sortKey && sort === c.sortKey ? (order === "asc" ? "ascending" : "descending") : undefined}
                >
                  {c.sortKey && onSortChange ? (
                    <SortHeader
                      label={c.header}
                      active={sort === c.sortKey}
                      order={order}
                      onClick={() => onSortChange(c.sortKey as string, sort === c.sortKey && order === "desc" ? "asc" : "desc")}
                    />
                  ) : (
                    c.header
                  )}
                </TableHead>
              ))}
              {rowActions?.length ? <TableHead className="w-10 pr-3" aria-label="Actions" /> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {error && !rows ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={colCount} className="p-3">
                  <ErrorPanel error={error} onRetry={onRetry} resource={props.resourceName} compact />
                </TableCell>
              </TableRow>
            ) : isPending && !rows ? (
              Array.from({ length: 8 }, (_, r) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: placeholders
                <TableRow key={r} aria-hidden>
                  {Array.from({ length: colCount }, (_, c) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: placeholders
                    <TableCell key={c}>
                      <Skeleton className="h-3.5" style={{ width: `${40 + ((r * 7 + c * 11) % 50)}%` }} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows && rows.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={colCount}>
                  <EmptyState icon={empty.icon} title={empty.title} description={empty.description} />
                </TableCell>
              </TableRow>
            ) : (
              rows?.map((row) => {
                const key = rowKey(row);
                return (
                  <TableRow
                    key={key}
                    data-state={selected.has(key) ? "selected" : undefined}
                    className={cn(onRowClick && "cursor-pointer")}
                    onClick={onRowClick ? (e) => !(e.target as HTMLElement).closest("a,button,[role=checkbox],[role=menuitem]") && onRowClick(row) : undefined}
                  >
                    {selectable ? (
                      <TableCell className="pl-3">
                        <Checkbox
                          aria-label="Select row"
                          checked={selected.has(key)}
                          onCheckedChange={(v) =>
                            setSelected((s) => {
                              const n = new Set(s);
                              if (v) n.add(key);
                              else n.delete(key);
                              return n;
                            })
                          }
                        />
                      </TableCell>
                    ) : null}
                    {visible.map((c, i) => (
                      <TableCell key={c.id} className={cn(c.align === "right" && "text-right tabular", i === 0 && !selectable && "pl-3", c.className)}>
                        {c.cell(row)}
                      </TableCell>
                    ))}
                    {rowActions?.length ? (
                      <TableCell className="pr-3 text-right">
                        <RowActionsMenu row={row} actions={rowActions} />
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      {props.page !== undefined && props.pageSize !== undefined && props.total !== undefined && props.onPageChange ? (
        <Pagination
          page={props.page}
          pageSize={props.pageSize}
          total={props.total}
          onPageChange={props.onPageChange}
          onPageSizeChange={props.onPageSizeChange}
          fetching={props.isFetching && !isPending}
        />
      ) : null}
    </div>
  );
}

function RowActionsMenu<T>({ row, actions }: { row: T; actions: RowAction<T>[] }) {
  const list = actions.filter((a) => !a.hidden?.(row));
  if (!list.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger render={<Button size="icon-xs" variant="ghost" aria-label="Row actions" />}>
        <EllipsisVertical />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {list.map((a, i) => (
          <span key={a.label}>
            {a.destructive && i > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuItem variant={a.destructive ? "destructive" : "default"} onClick={() => a.onSelect(row)}>
              {a.icon ? <a.icon /> : null}
              {a.label}
            </DropdownMenuItem>
          </span>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
